import {useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useCalcContext} from "../../contexts/CalcContext";
import {useListingsSelection} from "../../contexts/ListingsSelectionContext";
import fetcher from "../../fetcher";

// Two clicks landing inside this window count as one. The card's `disabled`
// state already covers the async path (the getListing round-trip keeps the
// button disabled across a double-click), but the Listing Details button runs
// `skipFetch` — no await, so nothing would stop the second click from opening a
// second calculator tab.
const DOUBLE_CLICK_MS = 800

// The single implementation of "a listing was chosen". Shared by the listing
// card's Select button and the Listing Details "Select Vehicle" button so the
// two can never drift apart.
//
// `selectionOverride` is for callers that sit outside ListingsSelectionContext
// — notably ListingDetailModal, which the modals store renders at the HomePage
// level (a sibling of ListingsPageModal, not a descendant), so the provider is
// out of reach and the callback has to be handed down explicitly. When it's
// absent we fall back to the context, which is what the cards use.
export const useSelectListing = (selectionOverride) => {
    const {setCalcState} = useCalcContext()
    const contextSelection = useListingsSelection()
    const [selecting, setSelecting] = useState(false)
    const lastFiredAtRef = useRef(0)
    const inFlightRef = useRef(false)
    const location = useLocation()
    // On the standalone ListingsPage, Select opens the calculator in a new tab;
    // in the CalculatePage inventory drawer it fills the calculator inline.
    const onListingsPage = location.pathname.startsWith('/listings')

    const selection = selectionOverride || contextSelection

    // The combined MSRP (base + options) only lives on the detail endpoint
    // (Visor's /listings rows don't carry it), so fetch it on Select, then
    // report / fill accordingly. `skipFetch` is for Listing Details, which was
    // rendered from that very detail record and already holds combined_msrp and
    // inventory_type — re-fetching it would be a wasted round-trip.
    const selectListing = async (vehicle, {event, skipFetch = false} = {}) => {
        event?.stopPropagation()

        const now = Date.now()
        if (inFlightRef.current || (now - lastFiredAtRef.current) < DOUBLE_CLICK_MS) return
        lastFiredAtRef.current = now
        inFlightRef.current = true
        setSelecting(true)

        let msrp = vehicle.msrp
        let isUsed = false
        try {
            const detail = skipFetch ? vehicle : (await fetcher.getListing(vehicle.id))?.data
            const combined = detail?.combined_msrp
            if (combined != null) msrp = combined
            isUsed = String(detail?.inventory_type || '').toLowerCase() === 'used'
        } catch (_e) {
            // keep the list item's msrp as a fallback
        } finally {
            setSelecting(false)
            inFlightRef.current = false
        }

        const vin = vehicle.vin

        // Opened from a deal / calc (ListingsPageModal, CalcListingsDrawer):
        // report the chosen listing back to the opener (updates the deal / fills
        // the calculator) instead of the standalone new-tab flow.
        if (selection?.onSelectListing) {
            selection.onSelectListing({msrp, vin, isUsed, vehicle})
            return
        }

        if (onListingsPage) {
            // Open the calculator in a new tab, pre-filled with this vehicle's VIN
            // (drives the decode), combined MSRP, and new/used.
            const params = new URLSearchParams()
            if (vin) params.set('vin', vin)
            if (msrp != null) params.set('msrp', msrp)
            if (isUsed) params.set('is_used', 'true')
            window.open(`/calculate?${params.toString()}`, '_blank', 'noopener,noreferrer')
            return
        }

        if (msrp != null) {
            setCalcState((prev) => ({...prev, msrp}))
        }
        // On desktop #CalculatePage is its own scroll container (height:100vh +
        // overflow-y:auto); on mobile the window scrolls. Scroll both so the
        // calculator returns to the top regardless of layout.
        document.getElementById('CalculatePage')?.scrollTo({top: 0, behavior: 'smooth'})
        window.scrollTo({top: 0, behavior: 'smooth'})

        // Then focus the Invoice input. preventScroll keeps focus from fighting
        // the smooth scroll; the tick delay lets the state update settle first.
        setTimeout(() => {
            document.getElementById('calc-invoice-input')?.focus({preventScroll: true})
        }, 0)
    }

    return {selecting, selectListing}
}
